import { useEffect, useState } from 'react';
import { Button, Select, Upload, message, Form, Col, Row, Divider, Space, Typography, Drawer, Alert, Popconfirm } from 'antd';
import { CheckCircleOutlined, ContainerOutlined, LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ALLOWED_EXTENSIONS, ALLOWED_TYPES, applicationCode, drawerWidth, MAX_FILE_SIZE_MB, menuLinks, orderTypesCode } from '../../../utils/constants';
import { useUserAuth } from '../../../context/UserContext';
import { changeApplication } from '../../../utils/functions';
import { useSelector } from 'react-redux';
import CustomerOrdersServices from '../../../services/customerOrdersServices';
const { Text, Title } = Typography;

const UploadOrderModal = ({ visibility, onClose }: { visibility: boolean, onClose: () => void }) => {

    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { user, setActiveApp, applications } = useUserAuth();
    const templates = useSelector((state: any) => state.utils.templates);
    const salesOrganization = useSelector((state: any) => state.utils.salesOrganization);
    const quotatios = useSelector((state: any) => state.utils.quotatios);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [selectedOrganizationCountry, setSelectedOrganizationCountry] = useState(null);
    const [selectedQuotationDocument, setSelectedQuotationDocument] = useState(null);
    const [selectedOrderTypeCode, setSelectedOrderTypeCode] = useState(null);
    const [loadingUploadFile, setLoadingUploadFile] = useState(false);

    useEffect(() => {
        if (!visibility) {
            resetForm();
        } else {
            // Validate form when drawer opens
            handleFormChange();
        }
    }, [visibility]);

    // Re-validate when selectedOrderTypeCode changes (affects quotationID requirement)
    useEffect(() => {
        if (visibility) {
            // Trigger validation on quotationID field since its requirement changed
            form.validateFields(['quotationID']).then(() => {
                handleFormChange();
            }).catch(() => {
                handleFormChange();
            });
        }
    }, [selectedOrderTypeCode, visibility]);

    const resetForm = () => {
        setUploadSuccess(false);
        setIsFormValid(false);
        setSelectedOrganizationCountry(null);
        setSelectedQuotationDocument(null);
        setSelectedOrderTypeCode(null);
        form.resetFields();
    }

    const handleFormChange = () => {
        const allValues = form.getFieldsValue();

        // Check if any field has validation errors
        const errorList = form.getFieldsError();
        const hasErrors = errorList.some(({ errors }) => errors.length > 0);

        // Check required fields based on form rules
        // 1. templateID is always required
        const hasTemplate = allValues.templateID !== undefined && allValues.templateID !== null && allValues.templateID !== '';

        // 2. quotationID is conditionally required (only when selectedOrderTypeCode === orderTypesCode.zqs)
        const isQuotationRequired = selectedOrderTypeCode === orderTypesCode.zqs;
        const hasQuotation = !isQuotationRequired || (allValues.quotationID !== undefined && allValues.quotationID !== null && allValues.quotationID !== '');

        // 3. file field is an array, check if it has at least one item
        const hasFile = Array.isArray(allValues.file) && allValues.file.length > 0;

        // Form is valid if no errors and all required fields are filled
        setIsFormValid(!hasErrors && hasTemplate && hasQuotation && hasFile);
    };

    const normFile = (e: any) => {
        // Normalize file event to file list
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const beforeUpload = (file: any) => {
        const isExcelType = ALLOWED_TYPES.includes(file.type);
        const isExcelExt = ALLOWED_EXTENSIONS.some(ext =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!isExcelType && !isExcelExt) {
            message.error(t('messages.uploadTypeAllowed'));
            return Upload.LIST_IGNORE;
        }

        const isLt50MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt50MB) {
            message.error(t('messages.uploadSizeLimit'));
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const onQuotationSelect = (value: any, option: any) => {
        if (option && option.document) {
            setSelectedQuotationDocument(option.document);
        }
        else {
            setSelectedQuotationDocument(null);
            // message.error(t('messages.selectedQuotationDocumentError'));
        }
    }

    const onSubmitOrder = async () => {
        const values = form.getFieldsValue();

        const fileList = values.file;
        if (!fileList || fileList.length === 0) {
            message.error(t('messages.uploadFileEmpty'));
            return;
        }
        const file = fileList[0].originFileObj;
        const formData = new FormData();

        formData.append('File', file);
        formData.append('UserID', user.id);
        formData.append('TemplateID', values.templateID);
        formData.append('Document', selectedQuotationDocument as any);

        setUploadSuccess(false);
        setLoadingUploadFile(true);
        try {
            const respData = await CustomerOrdersServices.uploadOrdersFromFile(formData);

            if (respData.success) {
                resetForm();
                setUploadSuccess(true);
            } else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
        finally {
            setLoadingUploadFile(false);
        }
    };

    const goToUploadLogs = async () => {
        let comApp = applications.filter((item: any) => item.code === applicationCode.bulkImport)[0];
        let payload = {
            applicationID: comApp.value,
            userID: user.id
        }
        try {
            localStorage.setItem("activeApp", comApp.value);
            setActiveApp(comApp.value);
            await changeApplication(payload);
            window.location.replace(menuLinks.importLogs);
            // navigate(menuLinks.importLogs);
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
    }

    const checkSalesOrganization = (option: any) => {
        let selectedOrganization = salesOrganization.find((item: any) => item.id === option);
        setSelectedOrganizationCountry(selectedOrganization.countryCode);
    }

    return (
        <Drawer
            title={t('general.selectUpload')}
            width={drawerWidth}
            onClose={onClose}
            destroyOnClose={true}
            open={visibility}
        >

            {!uploadSuccess && (
                <Form
                    form={form}
                    layout='vertical'
                    name="uploadOrder"
                    autoComplete="off"
                    onFieldsChange={handleFormChange}
                >
                    <Row gutter={[16, 8]} className='mb10'>
                        {(selectedOrganizationCountry && selectedOrganizationCountry !== user.countryCode) && (
                            <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                                <Alert
                                    message={t('general.warning')}
                                    description={
                                        <Space direction='vertical'>
                                            <Text>{t('order.salesOrganizationCheck')}</Text>
                                            <Text>- {t('order.yourCountry')}: <Text strong>{user.countryCode} </Text> </Text>
                                            <Text>- {t('order.selectedOrgaizationCountry')}: <Text strong>{selectedOrganizationCountry} </Text></Text>
                                        </Space>
                                    }
                                    type="warning"
                                />
                            </Col>
                        )}

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
                            <Form.Item
                                label={t('order.template')}
                                name="templateID"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={t('general.select')}
                                    onChange={(value) => {
                                        const selectedTemplate = templates.find((item: any) => item.id === value);
                                        if (selectedTemplate) {
                                            setSelectedOrderTypeCode(selectedTemplate.orderTypeCode);
                                            form.setFieldsValue({
                                                quotationID: null
                                            });
                                        }
                                    }}
                                    options={templates.map((item: any) => ({ value: item.id, label: item.templateName }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }}>
                            <Form.Item
                                label={t('order.quotation')}
                                name="quotationID"
                                rules={[{ required: selectedOrderTypeCode === orderTypesCode.zqs ? true : false, message: t('messages.requiredField') }]}
                            // rules={[
                            // {
                            //     validator: (_, value) => {
                            //         // find the selected option from quotatios
                            //         const selected = quotatios.find((item: any) => item.id === value);
                            //         if (!selected || !selected.document) {
                            //             return Promise.reject(new Error(t('messages.selectedQuotationDocumentError')));
                            //         }
                            //         return Promise.resolve();
                            //     }
                            // }
                            // ]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={t('general.select')}
                                    options={quotatios.map((item: any) => ({ value: item.id, label: item.orderNumber, document: item.orderNumber }))}
                                    allowClear
                                    onChange={(value, option) => onQuotationSelect(value, option)}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('general.file')}
                                name="file"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Upload
                                    accept=".xls,.xlsx,.csv"
                                    maxCount={1}
                                    beforeUpload={beforeUpload}
                                    showUploadList={true}
                                    customRequest={({ file, onSuccess }) => {
                                        setTimeout(() => {
                                            onSuccess?.("ok");
                                        }, 500);
                                    }}
                                >
                                    <Button icon={<UploadOutlined />}>{t('order.selectFile')}</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <Form.Item>
                        <Popconfirm
                            title={t('order.confirmUpload')}
                            onConfirm={() => onSubmitOrder()}
                            okText={t('general.yes')}
                            cancelText={t('general.no')}
                        >
                            <Button 
                            disabled={!isFormValid || loadingUploadFile} 
                            htmlType='submit' 
                            block 
                            icon={loadingUploadFile ? <LoadingOutlined /> : <UploadOutlined />}
                            loading={loadingUploadFile}
                            >                            
                            {loadingUploadFile ? 'Uploading...' : t('order.startUpload')}</Button>
                        </Popconfirm>
                    </Form.Item>
                </Form>
            )}

            {uploadSuccess && (
                <Row gutter={[16, 8]} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ display: 'flex', justifyContent: 'center' }}>
                        <Space direction='vertical' align='center'>
                            <CheckCircleOutlined style={{ color: "#09b944", fontSize: 35 }} />
                            <Title level={5}>
                                {t('order.fileSubmitedCheck')}:
                                <Button onClick={() => goToUploadLogs()} style={{ marginLeft: 10 }} type="dashed" size='small' icon={<ContainerOutlined style={{ fontSize: 15 }} />}>
                                    {t('order.viewLogs')}
                                </Button>
                            </Title>
                        </Space>
                    </Col>
                </Row>
            )}
        </Drawer>
    );
}
export default UploadOrderModal;